/**
 * Every My VA route path. Values are relative to the app basename (`/my-va`,
 * set on the router in BlueSkyDashboard.jsx), which is what react-router
 * matches; use withBasename() for anything that needs a browser-visible URL.
 *
 * Only paths this app actually routes belong here. A destination that lives in
 * another app is an absolute URL, not a MY_VA_PATHS entry — see MY_HEALTH_URLS.
 *
 * Do NOT add routes at /my-va/submissions, /my-va/recommended-benefits or
 * /my-va/welcome-va-setup — those are other applications' rootUrls (see
 * content-build's registry.json) and resolve before this app.
 */
export const MY_VA_BASE = '/my-va';

export const MY_VA_PATHS = Object.freeze({
  HOME: '/',

  // Benefits
  BENEFITS: '/benefits',

  // Payments
  PAYMENTS: '/payments',
  PAYMENTS_OVERPAYMENTS: '/payments/overpayments',

  // Forms
  // Nested under /forms deliberately: /my-va/submissions is a separate app's
  // rootUrl (form-renderer), so a top-level /submissions would be unreachable.
  FORMS: '/forms',
  FORMS_SUBMISSIONS: '/forms/submissions',
  FORMS_UPLOAD: '/forms/upload',
  FORMS_UPLOAD_CLAIMANT: '/forms/upload/claimant',
  FORMS_UPLOAD_VETERAN: '/forms/upload/veteran',
  FORMS_UPLOAD_FAMILY: '/forms/upload/family',
  FORMS_UPLOAD_CONFIRMATION: '/forms/upload/confirmation',
});

/**
 * My HealtheVet destinations. These belong to another app, so they are absolute
 * and must never go through withBasename(). Shared by the side nav and the
 * landing page so the two cannot drift apart.
 */
export const MY_HEALTH_URLS = Object.freeze({
  HOME: '/my-health',
  APPOINTMENTS: '/my-health/appointments/',
  MESSAGES: '/my-health/secure-messages/inbox/',
  MEDICATIONS: '/my-health/medications',
  RECORDS: '/my-health/medical-records/',
  // Filed under Payments in the side nav, but the page is My HealtheVet's.
  TRAVEL_PAY: '/my-health/travel-pay/claims',
});

/**
 * Payments has some desinations outside of the /my-va app.
 */
export const PAYMENTS_URLS = Object.freeze({
  DEBTS: '/manage-va-debt/summary/debt-balances',
  COPAYS: '/manage-va-debt/summary/copay-balances',
  PAYMENT_HISTORY: '/va-payment-history/payments',
});

export const withBasename = path =>
  path === MY_VA_PATHS.HOME ? MY_VA_BASE : `${MY_VA_BASE}${path}`;

export const stripBasename = href =>
  (href.startsWith(MY_VA_BASE) ? href.slice(MY_VA_BASE.length) : href) ||
  MY_VA_PATHS.HOME;

// Whether a browser-visible href is a page this app routes, so a click on it
// can stay client-side. Only MY_VA_PATHS entries count, so another app's
// rootUrl under /my-va (e.g. /my-va/submissions) never matches.
export const isMyVaHref = href => {
  const pathname = href?.split(/[?#]/)[0];
  return Object.values(MY_VA_PATHS).some(path => {
    const fullPath = withBasename(path);
    return (
      pathname === fullPath ||
      pathname === `${fullPath}/` ||
      (path !== MY_VA_PATHS.HOME && pathname?.startsWith(`${fullPath}/`))
    );
  });
};

// Matches the `/forms/submissions/:formId` route in routes.jsx, where formId is the
// submission id (not the VA form number).
export const getFormDetailPath = formId =>
  `${MY_VA_PATHS.FORMS_SUBMISSIONS}/${formId}`;
